import { EventType, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import "@fontsource/dm-sans";
import { ToastCloseButton } from "components/shared/toast-close-button";
import { msalConfig } from "config/auth";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import { useCustomers } from "hooks/use-customers";
import { useUser } from "hooks/use-user";
import { api } from "lib/api";
import * as gtag from "lib/gtag";

import { GA_TRACKING_ID_PROD, GA_TRACKING_ID_TEST } from "config/constants";
import { isProd, isTest } from "config/env";
import "nanoreset";
import { DefaultSeo } from "next-seo";
import type { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import Script from "next/script";
import NextNProgress from "nextjs-progressbar";
import { useEffect } from "react";
import { CookiesProvider } from "react-cookie";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Slide, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { SWRConfig } from "swr";
import "../styles/globals.css";

dayjs.locale("en-gb");

export const msalInstance = new PublicClientApplication(msalConfig);

const accounts = msalInstance.getAllAccounts();

if (accounts.length > 0) {
  msalInstance.setActiveAccount(accounts[0]);
}

msalInstance.addEventCallback((event) => {
  // @ts-ignore
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
    // @ts-ignore
    const account = event.payload.account;
    msalInstance.setActiveAccount(account);
  }
});

const swrOptions = {
  dedupingInterval: 4000,
  revalidateOnFocus: false,
  fetcher: async (args: string | any[]) => {
    if (Array.isArray(args)) {
      const [path, query] = args;

      return api.url(path).query(query).get();
    }
    return api.url(args).get();
  },
};

const progressOptions = {
  showSpinner: false,
};

function GaTracker() {
  const router = useRouter();
  // Only read the cached list: fetching here would fire before the access
  // token is set on startup. The pages load /customers themselves.
  const { data: customers } = useCustomers({ revalidateOnMount: false });
  const { user } = useUser();

  useEffect(() => {
    if (!router.isReady) return;

    const customerId = gtag.getCustomerIdFromUrl(router.asPath);
    const customer = customers?.find((c) => c.id === customerId);
    const pageName = gtag.getPageName(router.asPath);

    gtag.pageview({
      url: router.asPath,
      customerId,
      customerName: customer?.name,
      pageName,
      userName: user?.name,
      userEmailDomain: user?.email?.split("@")[1],
      isInternalUser: user?.isInternalUser,
    });
  }, [router.isReady, router.asPath, customers, user]);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      const customerId = gtag.getCustomerIdFromUrl(url);
      const customer = customers?.find((c) => c.id === customerId);
      const pageName = gtag.getPageName(url);

      gtag.pageview({
        url,
        customerId,
        customerName: customer?.name,
        pageName,
        userName: user?.name,
        userEmailDomain: user?.email?.split("@")[1],
        isInternalUser: user?.isInternalUser,
      });
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events, customers, user]);

  return null;
}

function Bonver({ Component, pageProps: { ...pageProps } }: AppProps) {
  const router = useRouter();

  return (
    <>
      <Head>
        {(isTest || isProd) && (
          <script
            data-cookiecategory="statistics"
            dangerouslySetInnerHTML={{
              __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${isTest ? GA_TRACKING_ID_TEST : GA_TRACKING_ID_PROD}', {
                page_path: window.location.pathname,
              });
            `,
            }}
          />
        )}
      </Head>
      {(isTest || isProd) && (
        <Script
          data-cookiecategory="statistics"
          strategy="afterInteractive"
          src={`https://www.googletagmanager.com/gtag/js?id=${isTest ? GA_TRACKING_ID_TEST : GA_TRACKING_ID_PROD}`}
        />
      )}

      <DefaultSeo title="Bonver Logistics" />

      <NextNProgress
        color="#00ff7f"
        startPosition={0.1}
        stopDelayMs={200}
        options={progressOptions}
        height={2}
      />
      <SWRConfig value={swrOptions}>
        <CookiesProvider>
          <MsalProvider instance={msalInstance}>
            <GaTracker />
            <Component {...pageProps} />
            <ToastContainer
              position="top-right"
              autoClose={4000}
              hideProgressBar
              closeOnClick
              transition={Slide}
              closeButton={ToastCloseButton}
            />
          </MsalProvider>
        </CookiesProvider>
      </SWRConfig>
    </>
  );
}

export default Bonver;
